import { AutoSizer } from 'react-virtualized';
import {
  IBooking,
  ICalendarSelection,
  IResource,
  maxTime,
  minTime,
} from './Utils/EDCalendarViewUtils';
import { EDCalendarViewTimeView } from './Components/EDCalendarViewTimeView';
import { EDCalendarViewDayView } from './Components/EDCalendarViewDayView';
import { EDCalendarViewDayView2 } from './Components/EDCalendarViewDayView2';
import { EDCalendarViewMonthView } from './Components/EDCalendarViewMonthView';
import { EDCalendarViewTimeResourcesView } from './Components/EDCalendarViewTimeResourcesView';
import { incMin } from '../../Utils/dateUtils';
import { EDCalendarViewListView } from './Components/EDCalendarViewListView';
import { VirtuosoHandle } from 'react-virtuoso';

export type TEDCalendarViewType =
  | 'Day'
  | 'Week'
  | 'WorkWeek'
  | 'DayOverview'
  | 'WeekOverview'
  | 'WeekOverview2'
  | 'Month'
  | 'List';
export interface ICalendarProps {
  view: TEDCalendarViewType;
  currentDate: Date;
  bookings: IBooking[];
  allDayBookings: IBooking[];
  regularBookings: IBooking[];
  resources: IResource[];
  selection?: ICalendarSelection;
  setSelection?: (newSelection: ICalendarSelection | undefined) => void;
  onBookingClick?: (ref: EventTarget, booking: IBooking) => void;
  onSelectionChange?: (
    selectedStartTime: Date | undefined,
    selectedEndTime: Date | undefined,
    selectedResources: string[] | undefined
  ) => void;
  onSelectionClick?: (
    ref: EventTarget,
    selectedStartTime: Date | undefined,
    selectedEndTime: Date | undefined,
    selectedResources: string[] | undefined
  ) => void;
  /** Extra bottom spacing to clear a floating menu on touch devices */
  bottomSpacing?: number | string;
  /** Numeric inset to subtract from non-scrolling calendar view heights */
  bottomInsetPx?: number;
  /** Called with the date of the topmost visible day header as the list is scrolled */
  onScrolledDateChange?: (date: Date) => void;
  listViewRef?: React.RefObject<VirtuosoHandle | null>;
}

export const EDCalendarView: React.FC<ICalendarProps> = props => {
  const onSelectionClick = (
    ref: EventTarget | null,
    selStart: Date | undefined,
    selEnd: Date | undefined,
    selResources: string[] | undefined
  ) => {
    if (ref && props.onSelectionClick) {
      const start = selStart && selEnd ? minTime(selStart, selEnd) : selStart;
      const end =
        selStart && selEnd ? incMin(maxTime(selStart, selEnd), 30) : undefined;
      if (ref && props.onSelectionChange) {
        props.onSelectionChange(start, end, selResources);
      }
      props.onSelectionClick(ref, start, end, selResources);
    }
  };
  const onSelectionChange = (
    selStart: Date | undefined,
    selEnd: Date | undefined,
    selResources: string[] | undefined
  ) => {
    if (props.setSelection) {
      props.setSelection(
        selStart
          ? {
              selStart: selStart,
              selEnd: selEnd,
              selResources: selResources,
            }
          : undefined
      );
    }
    if (props.onSelectionChange) {
      const start = selStart && selEnd ? minTime(selStart, selEnd) : selStart;
      const end =
        selStart && selEnd ? incMin(maxTime(selStart, selEnd), 30) : undefined;
      props.onSelectionChange(start, end, selResources);
    }
  };
  const onBookingClick = (ref: EventTarget | null, booking: IBooking) => {
    if (ref && props.onBookingClick) {
      props.onBookingClick(ref, booking);
    }
  };

  const { view, ...viewProps } = props;
  const selProps = {
    selStart: props.selection?.selStart,
    selEnd: props.selection?.selEnd,
    selResources: props.selection?.selResources,
    onSelectionClick: onSelectionClick,
    onSelectionChange: onSelectionChange,
    onBookingClick: onBookingClick,
  };
  const dayCount =
    view === 'Day' ? 1 : view === 'WorkWeek' ? 5 : view === 'List' ? 60 : 7;

  return (
    <AutoSizer>
      {({ height, width }) => (
        <>
          {view === 'DayOverview' ? (
            <EDCalendarViewTimeResourcesView
              width={width}
              height={height}
              {...viewProps}
              {...selProps}
            />
          ) : view === 'WeekOverview' ? (
            <EDCalendarViewDayView
              width={width}
              height={height}
              {...viewProps}
              {...selProps}
            />
          ) : view === 'WeekOverview2' ? (
            <EDCalendarViewDayView2
              width={width}
              height={height}
              {...viewProps}
              {...selProps}
            />
          ) : view === 'Month' ? (
            <EDCalendarViewMonthView
              width={width}
              height={height}
              {...viewProps}
              {...selProps}
            />
          ) : view === 'List' ? (
            <EDCalendarViewListView
              width={width}
              height={height}
              dayCount={dayCount}
              {...viewProps}
              {...selProps}
            />
          ) : (
            <EDCalendarViewTimeView
              width={width}
              height={height}
              dayCount={dayCount}
              {...viewProps}
              {...selProps}
            />
          )}
        </>
      )}
    </AutoSizer>
  );
};
